import axios from 'axios'
import moment from 'moment'
import 'moment/min/locales'
import 'url-search-params-polyfill'
import apiConfiguration from '../utilities/api/apiConfiguration'

// GA events handler
import triggerCustomGaEvent from '../utilities/analytics/triggerCustomGaEvent'

// setActiveStep for query based selection
import fetchExchangeRates from './fetchExchangeRates'
import setActiveRoom from './setActiveRoomActions'
import fetchHotelDescriptiveInfo from './fetchHotelDescriptiveInfo'
import fetchEnhancementsData from './fetchEnhancements'
import setActiveStep from './activeStepActions'
import setActivePromo from './activePromoActions'
import manageMultiRoom from './multiRoomActions'

import _ from 'lodash'
import yn from 'yn'

import getCookie from '../utilities/session/getCookie'
import getLanguageLocale from '../utilities/language/getLanguageLocale'
import tripTeaseHotelSearch from '../utilities/tripTease/tripTeaseHotelSearch'
import tripTeaseHotelRoomOffer from '../utilities/tripTease/tripTeaseHotelRoomOffer'
import tripTeaseHotelIdentification from '../utilities/tripTease/tripTeaseHotelIdentification'

// availabilityData actions
export const FETCH_AVAILABILITY_BEGIN   = 'FETCH_AVAILABILITY_BEGIN'
export const FETCH_AVAILABILITY_SUCCESS = 'FETCH_AVAILABILITY_SUCCESS'
export const FETCH_AVAILABILITY_FAILURE = 'FETCH_AVAILABILITY_FAILURE'
export const SET_CURRENT_QUERY = 'SET_CURRENT_QUERY'
export const SET_PROPERTY_POLICIES = 'SET_PROPERTY_POLICIES'

export const fetchAvailabilityBegin = urlParams => ({
	type: FETCH_AVAILABILITY_BEGIN,
	payload: urlParams
})

export const fetchAvailabilitySuccess = availability => ({
	type: FETCH_AVAILABILITY_SUCCESS,
	payload: availability
})

export const fetchAvailabilityError = error => ({
	type: FETCH_AVAILABILITY_FAILURE,
	payload: { error }
})

export const setCurrentQuery = query => ({
	type: SET_CURRENT_QUERY,
	payload: { query }
})

export const setPropertyPolicies = propertyPolicies => ({
	type: SET_PROPERTY_POLICIES,
	payload: propertyPolicies
})

// Handle Availability Fetch & Functionality
export default function fetchAvailabilityData(roomGroupKey) {

	return (dispatch, getState) => {
		const configData = getState().configData

		// Init flags & initial steps
		let activeStep = 0,
				isActiveRoom = false,
				isRatePlanSet = false,
				isPromoCodeSet = false,
				isExactMatchSet = false,
				isMultiRoom = false,
				activeRoomCode = '',
				activeRoomData = [],
				activeRateCode = '',
				activePromoId = '',
				multiRoomTotal = null,
				adultsVal = 1,
				childrenVal = 0,
				numRoomsVal = 1

		// Import Query string & Parse URL
		const queryString = require('query-string')
		const urlQuery = queryString.parse(window.location.search)

		// Window.session data for guests / pets
		let flowGuestData = null

		if (localStorage.flowGuestData) {
			flowGuestData = JSON.parse(localStorage.flowGuestData)
		}

		// Client ID key used by IPS to pull correct brand data
		let currentClientId = null

		if (
			process.env.REACT_APP_FLOW_CLIENT_ID &&
			process.env.REACT_APP_FLOW_CLIENT_ID !== 'null'
		) {
			currentClientId = process.env.REACT_APP_FLOW_CLIENT_ID
		}

		// Set default lang
		let currentLanguage = 'en',
				currentCurrency = 'USD'

		// Use lang query param
		if (urlQuery.primaryLangId) {
			currentLanguage = urlQuery.primaryLangId
		}

		const currentLocale = getLanguageLocale(currentLanguage)
		moment.locale(currentLocale)

		// Use currency query param
		if (urlQuery.currency) {
			currentCurrency = urlQuery.currency
		}

		// Today flags for deep linking
		const startDateMissing = !urlQuery.startDate
		const endDateMissing = !urlQuery.endDate
		const hotelCodeMissing = !urlQuery.hotelCode
		const hotelProviderMissing = !urlQuery.hotelProvider && !urlQuery.provider

		let startDateVal = urlQuery.startDate,
				endDateVal = urlQuery.endDate

		const nightsVal = urlQuery.nights ? Number(urlQuery.nights) : 1

		if (
			urlQuery.startDate &&
			urlQuery.startDate.includes('TODAY')
		) {
			startDateVal = moment().format('YYYY-MM-DD')
		}

		if (
			urlQuery.endDate &&
			urlQuery.endDate.includes('TODAY')
		) {
			endDateVal = moment().add(urlQuery.endDate.replace('TODAY', ''), 'days').format('YYYY-MM-DD')
		}

		if (startDateMissing) {
			startDateVal = moment().format('YYYY-MM-DD')
		}

		if (endDateMissing) {
			endDateVal = moment(startDateVal).add(nightsVal, 'day').format('YYYY-MM-DD')
		}

		const defaultHotelCode = process.env.REACT_APP_COMPONENT_PROPERTY_DEFAULT_HOTEL_CODE
		const defaultHotelProvider = process.env.REACT_APP_COMPONENT_PROPERTY_DEFAULT_PROVIDER

		let appliedHotelCode = false
		let appliedHotelProvider = false

		if (hotelCodeMissing && defaultHotelCode && defaultHotelCode !== 'null') {
			urlQuery.hotelCode = defaultHotelCode
			appliedHotelCode = true
		}

		if (hotelProviderMissing && defaultHotelProvider && defaultHotelProvider !== 'null') {
			urlQuery.hotelProvider = defaultHotelProvider
			appliedHotelProvider = true
		}

		if (startDateMissing || endDateMissing || appliedHotelCode || appliedHotelProvider) {
			const updatedQuery = {
				...urlQuery,
				startDate: startDateVal,
				endDate: endDateVal,
			}
			const newSearch = queryString.stringify(updatedQuery)
			window.history.replaceState({}, '', `${window.location.pathname}?${newSearch}`)
		}

		// Set by session OR query with fallback
		if (
			!_.isEmpty(flowGuestData) &&
			flowGuestData[roomGroupKey].adults
		) {
			adultsVal = Number(flowGuestData[roomGroupKey].adults)
		} else if (urlQuery.adults) {
			adultsVal = Number(urlQuery.adults)
		}

		// Set by session OR query with fallback
		if (
			!_.isEmpty(flowGuestData) &&
			flowGuestData[roomGroupKey].children
		) {
			childrenVal = Number(flowGuestData[roomGroupKey].children)
		} else if (urlQuery.children) {
			childrenVal = Number(urlQuery.children)
		}

		if (
			urlQuery &&
			urlQuery.numRooms
		) {
			numRoomsVal = urlQuery.numRooms
		}

		// Init and set hotel provider VIA values from IPS config
		let hotelProvider,
				hotelTheme = null,
				currentProperty = null,
				sessionId = null

		if (
			getCookie('flowSessionId') &&
			getCookie('flowSessionId') !== ''
		) {
			sessionId = getCookie('flowSessionId')
		}

		if (!_.isEmpty(configData, '.configuration.hotel.options.properties')) {
			const currentProperties = configData.configuration.hotel.options.properties
			currentProperty = currentProperties.filter(function(property) {
				return String(property.hotelCode) === String(urlQuery.hotelCode)
			})
		}

		if (
			currentProperty &&
			currentProperty[0] &&
			Number(currentProperty[0].provider) >= 0
		) {
			hotelProvider = currentProperty[0].provider
		} else if (urlQuery.provider) {
			hotelProvider = urlQuery.provider
		} else if (urlQuery.hotelProvider) {
			hotelProvider = urlQuery.hotelProvider
		}

		if (
			!urlQuery.theme &&
			currentProperty &&
			currentProperty[0] &&
			currentProperty[0].theme
		) {
			hotelTheme = currentProperty[0].theme
		} else if (urlQuery.theme) {
			hotelTheme = urlQuery.theme
		}

		// URL Params
		let urlParams = {
			...urlQuery,
			accessible: urlQuery.accessible,
			adults: adultsVal,
			areaId: urlQuery.areaId,
			availReqType: urlQuery.availReqType,
			bestOnly: urlQuery.bestOnly,
			chainCode: urlQuery.chainCode,
			childAges: urlQuery.childAges,
			children: childrenVal,
			clientId: currentClientId,
			code: urlQuery.code,
			currency: currentCurrency,
			distance: urlQuery.distance,
			distanceMeasure: urlQuery.distanceMeasure,
			endDate: endDateVal,
			exactMatchOnly: urlQuery.exactMatchOnly,
			ga: urlQuery.ga,
			hotelCode: urlQuery.hotelCode,
			hotelProvider: hotelProvider,
			hotelStayOnly: urlQuery.hotelStayOnly,
			itineraryBooking: urlQuery.itineraryBooking,
			lat: urlQuery.lat,
			locale: urlQuery.locale,
			long: urlQuery.long,
			nights: urlQuery.nights,
			numRooms: urlQuery.numRooms,
			pricingMethod: urlQuery.pricingMethod,
			primaryLangId: currentLanguage,
			promoCode: urlQuery.promoCode,
			qt_tracking_id: urlQuery.qt_tracking_id,
			ratePlanCode: urlQuery.ratePlanCode,
			resId: urlQuery.resId,
			roomTypeCode: urlQuery.roomTypeCode,
			sortPriceOrder: urlQuery.sortPriceOrder,
			startDate: startDateVal,
			summaryOnly: urlQuery.summaryOnly,
			theme: hotelTheme,
			toggleRoomDetails: urlQuery.toggleRoomDetails,
			toggleSpecialOffers: urlQuery.toggleSpecialOffers,
			tracking_id: urlQuery.tracking_id,
			tt_ad_type: urlQuery.tt_ad_type,
			tt_adults: urlQuery.tt_adults,
			tt_checkin: urlQuery.tt_checkin,
			tt_checkout: urlQuery.tt_checkout,
			tt_children: urlQuery.tt_children,
			tt_clid: urlQuery.tt_clid,
			tt_date_type: urlQuery.tt_date_type,
			tt_gha_campaign_id: urlQuery.tt_gha_campaign_id,
			tt_gha_user_list_id: urlQuery.tt_gha_user_list_id,
			tt_los: urlQuery.tt_los,
			tt_meta: urlQuery.tt_meta,
			tt_partner_id: urlQuery.tt_partner_id,
			tt_user_country: urlQuery.tt_user_country,
			tt_user_device: urlQuery.tt_user_device,
			utm_campaign: urlQuery.utm_campaign,
			utm_content: urlQuery.utm_content,
			utm_medium: urlQuery.utm_medium,
			utm_source: urlQuery.utm_source,
			utm_term: urlQuery.utm_term,
		}

		// Fetch Query string method
		const fetchQuery = new URLSearchParams(),
					appQuery = new URLSearchParams()

		// Loop urlParams for undefined values & remove
		for (let key in urlParams) {
			if (typeof urlParams[key] === 'undefined') continue
			fetchQuery.append(key, urlParams[key])

			if (
				key !== 'adults' &&
				key !== 'children' &&
				urlParams[key] !== 'undefined'
			) {
				appQuery.append(key, urlParams[key])
			}

			if (
				key === 'roomTypeCode' &&
				urlParams[key] !== 'undefined'
			) {
				isActiveRoom = true
				activeRoomCode = urlParams[key]
			}

			if (
				key === 'ratePlanCode' &&
				urlParams[key] !== 'undefined'
			) {
				isRatePlanSet = true
				activeRateCode = urlParams[key]
			}

			if (
				key === 'promoCode' &&
				urlParams[key] !== 'undefined'
			) {
				isPromoCodeSet = true
				activePromoId = urlParams[key]
			}

			if (
				key === 'exactMatchOnly' &&
				urlParams[key] === 'TRUE'
			) {
				isExactMatchSet = true
			}

			if (
				key ==='numRooms' &&
				urlParams[key] > 1
			) {
				isMultiRoom  = true
				multiRoomTotal = parseInt(urlParams[key], 0)

				dispatch(manageMultiRoom(isMultiRoom, multiRoomTotal, 0))
			}
		}

		// Base API path
		let apiBase = process.env.REACT_APP_FLOW_ENDPOINT_URL + '/api/hotel/avail-rooms?'

		// Combine API URL & formatted urlParams
		const fetchResult = apiBase + fetchQuery.toString() + '&sessionId=' + sessionId

		// Dispatch initial GA events
		triggerCustomGaEvent('analytics-pageName', document.title)

		if (
			!_.isEmpty(flowGuestData) &&
			flowGuestData[roomGroupKey].adults &&
			flowGuestData[roomGroupKey].children
		) {
			dispatch(setCurrentQuery(appQuery.toString()))
		} else {
			dispatch(setCurrentQuery(fetchQuery.toString()))
		}

		dispatch(fetchAvailabilityBegin(urlParams))
		return axios.get(fetchResult, apiConfiguration())
			.then((response) => {
				const availability = response.data['hydra:member'][0].roomStays.map(availability => {
					return availability
				})

				// Dispatch initial GA events
				triggerCustomGaEvent('analytics-availableRoomTypes', availability)

				// Key availability objects by room group
				const keyedAvailability = {}
							keyedAvailability[roomGroupKey] = availability

				// Store hotel policies to be displayed from initial availability or secondary room/rate details requests (TARS)
				if (
					_.get(keyedAvailability, '[0][0].viewExtensions.policies') &&
					!_.isEmpty(keyedAvailability[0][0].viewExtensions.policies)
				) {
					dispatch(setPropertyPolicies(keyedAvailability[0][0].viewExtensions.policies))
				}

				dispatch(fetchAvailabilitySuccess(keyedAvailability))

				// Dispatch TT events if enabled for property
				if (
					yn(process.env.REACT_APP_COMPONENT_ENABLE_TRIPTEASE) &&
					keyedAvailability &&
					keyedAvailability[0] &&
					keyedAvailability[0][0]
				) {
					tripTeaseHotelSearch(startDateVal, endDateVal, adultsVal, childrenVal, numRoomsVal)
					tripTeaseHotelIdentification(currentProperty, keyedAvailability[0][0])
					tripTeaseHotelRoomOffer(keyedAvailability[0][0])
				}

				// Check promoCode is true
				if (
					isPromoCodeSet &&
					isExactMatchSet
				) {

					// Search ratePlans for rateCodes with activePromoId
					const activePromoRateCodes = availability[0]['ratePlans'].filter(function(promoRateCodes) {
						return promoRateCodes['ratePlanId'] === activePromoId
					})

					// Construct activePromoId & activePromoRateCodes
					let activePromoData = {
						activePromoID: activePromoId,
						activePromoCodes: activePromoRateCodes
					}

					// Dispatch activePromoData to state
					dispatch(setActivePromo(activePromoData))
				}

				// Check activeRoom is true
				if (
					yn(process.env.REACT_APP_COMPONENT_ENABLE_PROGRAMMATIC_ROOM_RATE_SELECTION) &&
					isActiveRoom &&
					isRatePlanSet
				) {
					let activeGroup = {}

					// Filter Synxis response for valid rates and rooms
					if (
						availability[0]['roomTypes'] &&
						availability[0]['ratePlans'] &&
						_.get(availability[0]['roomTypes'], '[0].viewExtensions.roomRates')
					) {
						// Filter roomTypes for activeRoom value
						const activeRoomType = availability[0]['roomTypes'].filter(function (roomTypes) {
							return roomTypes['roomTypeCode'] === activeRoomCode
						})

						// Filter roomRates for activeRoom value
						const activeRoomRate = availability[0]['roomTypes'][0].viewExtensions.roomRates.filter(function (roomRates) {
							return roomRates['roomTypeCode'] === activeRoomCode && roomRates['ratePlanCode'] === activeRateCode
						})

						// Filter ratePlans for activeRate value
						const activeRatePlan = availability[0]['ratePlans'].filter(function (ratePlans) {
							return ratePlans['ratePlanCode'] === activeRateCode
						})

						// Check filtered arrays ensure no empty data and push user into second step
						if (
							!_.isEmpty(activeRoomType) &&
							!_.isEmpty(activeRoomRate) &&
							!_.isEmpty(activeRatePlan)
						) {

							// Combine activeRoomType & activeRoomRate
							activeRoomData.push(activeRoomType, activeRoomRate, activeRatePlan)

							activeGroup[0] = activeRoomData

							// Import: setActiveRoom & Dispatch state: setActiveRoom
							dispatch(setActiveRoom(activeGroup))
							dispatch(fetchEnhancementsData(urlParams, activeRoomType[0].roomTypeCode, activeRatePlan[0].ratePlanCode, 0))

							// Update activeStep to payment
							activeStep = activeStep + 1
						}
					}
				}

				// Trigger hotelDescriptiveInfo call based on config flag
				if (
					yn(process.env.REACT_APP_COMPONENT_BASE_CRS_DATA) &&
					!yn(process.env.REACT_APP_COMPONENT_ENABLE_PROFILE_SERVICES)
				) {
					dispatch(fetchHotelDescriptiveInfo())
				}

				// Trigger initial FOREX call based on CRS currencyCode
				if (
					yn(process.env.REACT_APP_COMPONENT_ENABLE_FOREX) &&
					keyedAvailability &&
					keyedAvailability[0] &&
					keyedAvailability[0][0]
				) {
					let crsCurrencyCode = null

					for (const roomKey in keyedAvailability[0][0].roomTypes) {
						const currentRoom = keyedAvailability[0][0].roomTypes[roomKey]

						if (
							currentRoom &&
							currentRoom.viewExtensions &&
							currentRoom.viewExtensions.roomRates &&
							_.get(currentRoom.viewExtensions.roomRates, '[0].rates[0].total.currencyCode')
						) {
							crsCurrencyCode = currentRoom.viewExtensions.roomRates[0].rates[0].total.currencyCode
						}
					}

					if (crsCurrencyCode) {
						dispatch(fetchExchangeRates(crsCurrencyCode))
					}
				}

				dispatch(setActiveStep(activeStep))

			})
			.catch(error => dispatch(fetchAvailabilityError(error)))
	}
}


// WEBPACK FOOTER //
// ./src/actions/fetchAvailability.js